/**
 * AccountantProfilePage: dedicated profile page for a Novala-certified accountant.
 *
 * Reads :id from the URL, pulls the accountant via useAccountantById.
 * Shows hero (avatar + identity + Contact button), About, Services,
 * Industries, Novala certifications, inline At a glance stats (no
 * boxes), and a Phase B reviews placeholder. Fields hide gracefully
 * when the accountant left them blank; nothing shows "Not provided".
 *
 * Contact action is a Phase A placeholder (window.alert); Phase B
 * wires it to the real email send service.
 */

import { useId, useMemo } from "react";
import {
  Link,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  ArrowLeft,
  Award,
  CheckCircle2,
  Clock,
  Mail,
  MapPin,
  Users,
} from "lucide-react";
import { useAccountantById } from "../../../../../hooks/useFindAccountants";
import {
  INDUSTRY_LABELS,
  PRODUCT_LABELS,
  SERVICE_LABELS,
} from "../findAccountantMockData";
import { getInitials } from "../../myAccountantUtils";

export function AccountantProfileHeader() {
  const navigate = useNavigate();
  const match = useMatch("/accounting/my-accountant/find/:id");
  const id = match?.params.id;
  const { data } = useAccountantById(id);
  const displayName = data?.name ?? "Profile";

  return (
    <div style={headerBarStyle}>
      <button
        type="button"
        style={backBtnStyle}
        onClick={() => navigate("/accounting/my-accountant/find")}
        aria-label="Back to Find an accountant"
      >
        <ArrowLeft size={13} strokeWidth={2.2} />
        Back
      </button>
      <nav style={crumbsStyle} aria-label="Breadcrumb">
        <Link to="/accounting" style={crumbLinkStyle}>
          Accounting
        </Link>
        <span style={crumbSepStyle}>/</span>
        <Link to="/accounting/my-accountant" style={crumbLinkStyle}>
          My accountant
        </Link>
        <span style={crumbSepStyle}>/</span>
        <Link to="/accounting/my-accountant/find" style={crumbLinkStyle}>
          Find an accountant
        </Link>
        <span style={crumbSepStyle}>/</span>
        <span style={crumbCurrentStyle}>{displayName}</span>
      </nav>
    </div>
  );
}

export default function AccountantProfilePage() {
  const uid = useId().replace(/:/g, "");
  const styleTag = useProfileStyles(uid);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data } = useAccountantById(id);

  const verifiedSince = useMemo(() => {
    if (!data?.verifiedAt) return "";
    const d = new Date(data.verifiedAt);
    return d.toLocaleDateString("en-CA", { month: "long", year: "numeric" });
  }, [data?.verifiedAt]);

  if (!data) {
    return (
      <>
        {styleTag}
        <div className={`ap-${uid}-missing`}>
          <h2 className={`ap-${uid}-missing-title`}>Accountant not found</h2>
          <p className={`ap-${uid}-missing-body`}>
            That profile might have been removed or the link is incorrect.
          </p>
          <button
            type="button"
            className={`ap-${uid}-missing-cta`}
            onClick={() => navigate("/accounting/my-accountant/find")}
          >
            Back to directory
          </button>
        </div>
      </>
    );
  }

  const initials = getInitials(data.name, data.email ?? data.name);

  const handleContact = () => {
    window.alert(
      `Phase A: contact flow for ${data.name} ships in Phase B.`,
    );
  };

  const industriesLine = data.industries
    .map((i) => INDUSTRY_LABELS[i])
    .join(", ");

  return (
    <>
      {styleTag}
      <article className={`ap-${uid}-page`}>
        <header className={`ap-${uid}-hero`}>
          <div className={`ap-${uid}-ava`} aria-hidden="true">
            {initials}
          </div>
          <div className={`ap-${uid}-ident`}>
            <h1 className={`ap-${uid}-name`}>
              {data.name}
              <CheckCircle2
                size={16}
                strokeWidth={2.4}
                className={`ap-${uid}-verified`}
                aria-label="Novala-verified"
              />
            </h1>
            <p className={`ap-${uid}-firm`}>{data.firmName}</p>
            <p className={`ap-${uid}-loc`}>
              <MapPin size={12} strokeWidth={2} />
              <span>
                {data.city}, {data.province}
                <span className={`ap-${uid}-dot`}>&middot;</span>
                <strong>{data.distanceKm} km</strong> from your business
                {verifiedSince && (
                  <>
                    <span className={`ap-${uid}-dot`}>&middot;</span>
                    Novala-verified since <strong>{verifiedSince}</strong>
                  </>
                )}
              </span>
            </p>
          </div>
          <div className={`ap-${uid}-hero-actions`}>
            <button
              type="button"
              className={`ap-${uid}-btn-primary`}
              onClick={handleContact}
            >
              <Mail size={13} strokeWidth={2.2} />
              Contact
            </button>
          </div>
        </header>

        {data.bio && (
          <section className={`ap-${uid}-section`}>
            <p className={`ap-${uid}-sh`}>About</p>
            <p className={`ap-${uid}-bio`}>{data.bio}</p>
          </section>
        )}

        {data.services.length > 0 && (
          <section className={`ap-${uid}-section`}>
            <p className={`ap-${uid}-sh`}>Services provided</p>
            <ul className={`ap-${uid}-grid`}>
              {data.services.map((slug) => (
                <li key={slug} className={`ap-${uid}-item`}>
                  <span className={`ap-${uid}-item-icon`}>
                    <CheckCircle2 size={14} strokeWidth={2.4} />
                  </span>
                  <span className={`ap-${uid}-item-label`}>
                    {SERVICE_LABELS[slug]}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {data.industries.length > 0 && (
          <section className={`ap-${uid}-section`}>
            <p className={`ap-${uid}-sh`}>Industries served</p>
            <p className={`ap-${uid}-plain`}>{industriesLine}</p>
          </section>
        )}

        {data.products.length > 0 && (
          <section className={`ap-${uid}-section`}>
            <p className={`ap-${uid}-sh`}>Novala certifications</p>
            <div className={`ap-${uid}-certs`}>
              {data.products.map((p) => (
                <span key={p} className={`ap-${uid}-cert`}>
                  <CheckCircle2 size={12} strokeWidth={2.4} />
                  {PRODUCT_LABELS[p]}
                </span>
              ))}
            </div>
          </section>
        )}

        <section className={`ap-${uid}-section`}>
          <p className={`ap-${uid}-sh`}>At a glance</p>
          <div className={`ap-${uid}-stats`}>
            {data.yearsExperience > 0 && (
              <>
                <div className={`ap-${uid}-stat`}>
                  <span className={`ap-${uid}-stat-icon`} aria-hidden="true">
                    <Award size={16} strokeWidth={2} />
                  </span>
                  <span className={`ap-${uid}-stat-text`}>
                    <strong>{data.yearsExperience} years</strong>
                    in practice
                  </span>
                </div>
                <span className={`ap-${uid}-stat-sep`} />
              </>
            )}
            <div className={`ap-${uid}-stat`}>
              <span className={`ap-${uid}-stat-icon`} aria-hidden="true">
                <Users size={16} strokeWidth={2} />
              </span>
              <span className={`ap-${uid}-stat-text`}>
                <strong>Serving businesses</strong>
                in {data.city}
              </span>
            </div>
            <span className={`ap-${uid}-stat-sep`} />
            <div className={`ap-${uid}-stat`}>
              <span className={`ap-${uid}-stat-icon`} aria-hidden="true">
                <Clock size={16} strokeWidth={2} />
              </span>
              <span className={`ap-${uid}-stat-text`}>
                <strong>Response time</strong>
                shown once established
              </span>
            </div>
          </div>
        </section>

        <section className={`ap-${uid}-section`}>
          <p className={`ap-${uid}-sh`}>Reviews from other Novala businesses</p>
          <div className={`ap-${uid}-ph`}>
            Reviews will appear here once accountants have built up client
            history on Novala.
          </div>
        </section>
      </article>
    </>
  );
}

const headerBarStyle: React.CSSProperties = {
  padding: "14px 32px",
  borderBottom: "1px solid #E7EAF0",
  background: "#ffffff",
  display: "flex",
  alignItems: "center",
  gap: 14,
  flexShrink: 0,
};

const backBtnStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #D8DEE6",
  color: "#0E1A1A",
  padding: "6px 11px 6px 9px",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const crumbsStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  fontSize: 12.5,
};

const crumbLinkStyle: React.CSSProperties = {
  color: "#6B7684",
  textDecoration: "none",
  fontWeight: 500,
};

const crumbSepStyle: React.CSSProperties = {
  color: "#B0B9C3",
  fontWeight: 400,
};

const crumbCurrentStyle: React.CSSProperties = {
  color: "#0E1A1A",
  fontWeight: 600,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: 280,
};

function useProfileStyles(uid: string) {
  const css = `
    .ap-${uid}-page {
      max-width: 920px;
      margin: 0 auto;
      padding: 0;
    }

    .ap-${uid}-hero {
      display: grid;
      grid-template-columns: 88px 1fr auto;
      gap: 22px;
      align-items: flex-start;
      padding: 8px 0 28px;
      border-bottom: 1px solid #F3F5F5;
    }
    .ap-${uid}-ava {
      width: 88px; height: 88px;
      border-radius: 50%;
      background: #0E1A1A;
      color: #ffffff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 28px;
      letter-spacing: 0.02em;
      flex-shrink: 0;
    }
    .ap-${uid}-ident { min-width: 0; }
    .ap-${uid}-name {
      font-size: 22px;
      font-weight: 700;
      color: #0E1A1A;
      margin: 4px 0 6px;
      letter-spacing: -0.012em;
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
    .ap-${uid}-verified { color: #0F6E56; flex-shrink: 0; }
    .ap-${uid}-firm {
      font-size: 14px;
      color: #3A4A52;
      font-weight: 500;
      margin: 0 0 8px;
    }
    .ap-${uid}-loc {
      font-size: 12.5px;
      color: #6B7684;
      margin: 0;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
    }
    .ap-${uid}-loc svg { color: #6B7684; flex-shrink: 0; }
    .ap-${uid}-loc strong { color: #0E1A1A; font-weight: 600; margin: 0 2px; }
    .ap-${uid}-dot { color: #B0B9C3; margin: 0 6px; }

    .ap-${uid}-hero-actions {
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-width: 140px;
    }
    .ap-${uid}-btn-primary {
      background: #0E1A1A;
      color: #ffffff;
      border: 0;
      padding: 10px 18px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      transition: opacity 160ms, transform 160ms;
    }
    .ap-${uid}-btn-primary:hover {
      opacity: 0.92;
      transform: translateY(-1px);
    }

    .ap-${uid}-section {
      padding: 24px 0;
      border-bottom: 1px solid #F3F5F5;
    }
    .ap-${uid}-section:last-child { border-bottom: 0; }
    .ap-${uid}-sh {
      font-size: 11px;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      font-weight: 700;
      color: #6B7684;
      margin: 0 0 12px;
    }
    .ap-${uid}-bio {
      font-size: 13.5px;
      color: #3A4A52;
      line-height: 1.65;
      margin: 0;
      max-width: 720px;
    }
    .ap-${uid}-plain {
      font-size: 13.5px;
      color: #0E1A1A;
      margin: 0;
      line-height: 1.55;
    }

    .ap-${uid}-grid {
      list-style: none;
      padding: 0;
      margin: 0;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 2px;
    }
    .ap-${uid}-item {
      display: grid;
      grid-template-columns: 22px 1fr;
      gap: 10px;
      padding: 8px 0;
      align-items: flex-start;
    }
    .ap-${uid}-item-icon {
      color: #0F6E56;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-top: 2px;
    }
    .ap-${uid}-item-label {
      font-size: 13px;
      font-weight: 500;
      color: #0E1A1A;
    }

    .ap-${uid}-certs {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
    .ap-${uid}-cert {
      background: #F4FAF8;
      border: 1px solid #D5EBE2;
      padding: 7px 13px;
      border-radius: 10px;
      font-size: 12.5px;
      color: #0A5544;
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      gap: 7px;
    }
    .ap-${uid}-cert svg { color: #0F6E56; }

    .ap-${uid}-stats {
      display: flex;
      align-items: center;
      gap: 28px;
      flex-wrap: wrap;
    }
    .ap-${uid}-stat {
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }
    .ap-${uid}-stat-icon {
      color: #0F6E56;
      display: inline-flex;
      flex-shrink: 0;
    }
    .ap-${uid}-stat-text {
      font-size: 13.5px;
      color: #3A4A52;
      line-height: 1.3;
    }
    .ap-${uid}-stat-text strong {
      color: #0E1A1A;
      font-weight: 700;
      letter-spacing: -0.005em;
      margin-right: 4px;
    }
    .ap-${uid}-stat-sep {
      width: 1px;
      height: 20px;
      background: #E7EAF0;
    }

    .ap-${uid}-ph {
      background: #F3F5F5;
      border: 1px dashed #E7EAF0;
      border-radius: 8px;
      padding: 14px 16px;
      font-size: 12.5px;
      color: #6B7684;
      font-style: italic;
    }

    .ap-${uid}-missing {
      max-width: 480px;
      margin: 80px auto;
      text-align: center;
    }
    .ap-${uid}-missing-title {
      font-size: 20px;
      font-weight: 700;
      color: #0E1A1A;
      margin: 0 0 8px;
    }
    .ap-${uid}-missing-body {
      font-size: 13.5px;
      color: #6B7684;
      margin: 0 0 20px;
    }
    .ap-${uid}-missing-cta {
      background: #0E1A1A;
      color: #ffffff;
      border: 0;
      padding: 10px 18px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
    }

    @media (max-width: 640px) {
      .ap-${uid}-hero { grid-template-columns: 72px 1fr; }
      .ap-${uid}-ava { width: 72px; height: 72px; font-size: 24px; }
      .ap-${uid}-hero-actions { grid-column: 1 / -1; min-width: 0; margin-top: 8px; }
      .ap-${uid}-grid { grid-template-columns: 1fr; }
    }
  `;
  return <style>{css}</style>;
}